import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitMerge, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Founder-only tool: merge two duplicate user accounts.
 *   source_id  →  target_id  (source is soft-deleted after merge).
 */
export default function MergeUsersDialog({ users, open, onOpenChange, onDone }) {
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const pickable = useMemo(
    () => (users || []).filter((u) => !u.merged_into),
    [users]
  );

  const source = pickable.find((u) => u.id === sourceId);
  const target = pickable.find((u) => u.id === targetId);

  const runPreview = async () => {
    if (!sourceId || !targetId) return;
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/users/merge-preview", {
        source_id: sourceId,
        target_id: targetId,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const runMerge = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Merge "${source?.name || source?.email}" INTO "${target?.name || target?.email}"?\n\n` +
      `${preview.total_references} references will be re-pointed. The source account will be deactivated. This cannot be undone from the UI.`
    )) return;
    setMerging(true);
    try {
      await api.post("/admin/users/merge", {
        source_id: sourceId,
        target_id: targetId,
        confirm: "MERGE",
      });
      toast.success("Merge complete");
      setPreview(null);
      setSourceId("");
      setTargetId("");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMerging(false);
    }
  };

  const byCollTotal = preview
    ? Object.entries(preview.by_collection || {}).sort((a, b) =>
        String(a[0]).localeCompare(String(b[0])))
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="merge-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge size={18} className="text-[#F54800]" /> Merge duplicate identities
          </DialogTitle>
          <DialogDescription>
            Re-points every task, timesheet, review and record from a source account to a target one, then soft-deletes the source. Founder-only.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Source (will be merged away)</Label>
            <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); }}>
              <SelectTrigger data-testid="merge-source-select" className="mt-1">
                <SelectValue placeholder="Pick the duplicate account…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {pickable
                  .filter((u) => u.id !== targetId)
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email} <span className="text-slate-400 ml-1">· {u.role}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Target (canonical account)</Label>
            <Select value={targetId} onValueChange={(v) => { setTargetId(v); setPreview(null); }}>
              <SelectTrigger data-testid="merge-target-select" className="mt-1">
                <SelectValue placeholder="Pick the account to keep…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {pickable
                  .filter((u) => u.id !== sourceId)
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email} <span className="text-slate-400 ml-1">· {u.role}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-slate-500">
            After merge, the source account&apos;s login is disabled and its records show the target&apos;s name.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={!sourceId || !targetId || loading}
            onClick={runPreview}
            data-testid="merge-preview-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
            Preview impact
          </Button>
        </div>

        {preview && (
          <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="merge-preview-panel">
            <div className="flex items-center gap-2 text-sm mb-2">
              <AlertTriangle size={14} className="text-amber-600" />
              <span className="font-semibold">{preview.total_references}</span> records reference
              <span className="text-slate-700">{source?.name || source?.email}</span>
            </div>
            {byCollTotal.length === 0 ? (
              <div className="text-xs text-slate-500">No references found — safe to deactivate directly.</div>
            ) : (
              <div className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 max-h-52 overflow-auto">
                {byCollTotal.map(([coll, fields]) => (
                  <div key={coll} className="border-b border-slate-200/60 pb-1">
                    <div className="font-medium text-slate-800">{coll}</div>
                    <div className="text-slate-500">
                      {Object.entries(fields).map(([f, n]) => (
                        <span key={f} className="mr-2">
                          {f}: <span className="font-mono text-slate-700">{n}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="merge-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-[#F54800] hover:bg-[#D63E00] text-white"
            disabled={!preview || merging}
            onClick={runMerge}
            data-testid="merge-confirm-btn"
          >
            {merging ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <GitMerge size={14} className="mr-1.5" />}
            Merge & deactivate source
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
